import { Box, Tooltip, Typography } from '@mui/material';
import { TournamentStatus, TournamentTeamStatus } from '@okletsplay/core';
import { TournamentEntity } from 'app/interfaces/entity';
import { TournamentStatusProgress } from 'modules/tournaments/components/TournamentStatus/TournamentStatusIndicator';
import { TournamentStatusLabel } from 'modules/tournaments/components/TournamentStatus/TournamentStatusLabel';
import { TournamentTileDate } from 'modules/tournaments/components/TournamentStatus/TournamentTileDate';
import { TournamentStatusViewType } from 'modules/tournaments/enums';
import { useTeamPlace } from 'modules/tournaments/hooks';
import { getActiveTeamsCount } from 'modules/tournaments/hooks/use-tournament-team-details';
import React, { FC, ReactElement, useMemo } from 'react';

interface ComponentProps {
  tournament: TournamentEntity;
  viewType?: TournamentStatusViewType;
  showStatus?: boolean;
}

export const KnockoutTournamentStatus: FC<ComponentProps> = ({
  tournament,
  viewType = TournamentStatusViewType.Tile,
  showStatus = true,
}) => {
  const { teamPlace } = useTeamPlace(tournament);
  const isHeaderView = useMemo(() => viewType === TournamentStatusViewType.Header, [viewType]);
  const direction = useMemo(() => {
    if ([TournamentStatus.Pending, TournamentStatus.Checkin].includes(tournament?.status)) {
      return 'column';
    }

    return !isHeaderView ? 'column' : 'column-reverse';
  }, [tournament?.status, isHeaderView]);

  const isSinglePlayer = useMemo(() => {
    return tournament?.teamsSettings?.teamSize === 1;
  }, [tournament?.teamsSettings?.teamSize]);

  const teamsAmountLabel = useMemo(() => {
    if (!tournament) {
      return null;
    }

    return `${isSinglePlayer ? 'Players' : 'Teams'}: ${tournament.teams?.length} of ${
      tournament.teamsSettings?.maxTeams
    }`;
  }, [tournament, isSinglePlayer]);

  const joinedTeamsSize = useMemo(() => {
    if (tournament.status === TournamentStatus.Checkin) {
      return tournament.teams?.filter((team) => team.status === TournamentTeamStatus.Ready)?.length;
    }

    return tournament?.teams?.length ?? 0;
  }, [tournament.status, tournament?.teams]);

  const activeTeamsSize = useMemo(() => {
    if (tournament.status === TournamentStatus.Live) {
      return getActiveTeamsCount(tournament);
    }

    return tournament?.teams?.length ?? 0;
  }, [tournament]);

  const teamsLabel = useMemo<string>(() => {
    return `${isSinglePlayer ? 'Players' : 'Teams'}: ${joinedTeamsSize} of ${
      tournament?.teamsSettings?.maxTeams
    }`;
  }, [joinedTeamsSize, tournament?.teamsSettings?.maxTeams, isSinglePlayer]);

  const liveStatusTile = useMemo<ReactElement>(() => {
    return (
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        {Boolean(teamPlace) && <Typography variant="subtitle2">{`Rank: ${teamPlace}`}</Typography>}
        {isHeaderView ? (
          <TournamentStatusLabel status={TournamentStatus.Live} />
        ) : (
          <>
            <Typography variant="subtitle2">{`${
              isSinglePlayer ? 'Players' : 'Teams'
            }: ${activeTeamsSize} of ${joinedTeamsSize}`}</Typography>
            <TournamentTileDate startedAt={tournament?.startedAt} />
          </>
        )}
      </Box>
    );
  }, [
    teamPlace,
    isHeaderView,
    activeTeamsSize,
    joinedTeamsSize,
    tournament?.startedAt,
    isSinglePlayer,
  ]);

  const checkinStatusTile = useMemo<ReactElement>(() => {
    return (
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography variant="subtitle2">{`Ready: ${joinedTeamsSize} of ${tournament?.teamsSettings?.maxTeams}`}</Typography>
        {!isHeaderView && <TournamentTileDate startedAt={tournament?.startedAt} />}
      </Box>
    );
  }, [isHeaderView, joinedTeamsSize, tournament?.startedAt, tournament?.teamsSettings?.maxTeams]);

  const finishedStatusTile = useMemo<ReactElement>(() => {
    return (
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        {Boolean(teamPlace) && <Typography variant="subtitle2">{`Rank: ${teamPlace}`}</Typography>}
        {!Boolean(teamPlace) && (
          <Typography variant="subtitle2" pr={2}>
            {teamsLabel}
          </Typography>
        )}
        <TournamentTileDate startedAt={tournament?.startedAt} />
      </Box>
    );
  }, [teamPlace, teamsLabel, tournament?.startedAt]);

  const canceledStatusTile = useMemo<ReactElement>(() => {
    return (
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography variant="subtitle2" pr={2}>
          {teamsLabel}
        </Typography>
        <TournamentTileDate startedAt={tournament?.startedAt} />
      </Box>
    );
  }, [teamsLabel, tournament?.startedAt]);

  const headerLabelTemplate = useMemo<ReactElement>(() => {
    switch (tournament?.status) {
      case TournamentStatus.Pending:
        return <Typography variant={'subtitle2'}>{teamsAmountLabel}</Typography>;
      case TournamentStatus.Live:
        return liveStatusTile;
      case TournamentStatus.Checkin: {
        return checkinStatusTile;
      }
      case TournamentStatus.Finished:
      case TournamentStatus.Canceled: {
        return <TournamentStatusLabel status={tournament?.status} />;
      }
      default:
        return <></>;
    }
  }, [teamsAmountLabel, liveStatusTile, checkinStatusTile, tournament?.status]);

  const tileLabelTemplate = useMemo<ReactElement>(() => {
    switch (tournament?.status) {
      case TournamentStatus.Pending:
        return (
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Typography
              variant={'subtitle2'}
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {teamsAmountLabel}
            </Typography>
            <TournamentTileDate startedAt={tournament?.startedAt} />
          </Box>
        );
      case TournamentStatus.Live: {
        return liveStatusTile;
      }
      case TournamentStatus.Checkin: {
        return checkinStatusTile;
      }
      case TournamentStatus.Finished: {
        return finishedStatusTile;
      }
      case TournamentStatus.Canceled: {
        return canceledStatusTile;
      }
      default:
        return <></>;
    }
  }, [
    tournament?.status,
    tournament?.startedAt,
    teamsAmountLabel,
    liveStatusTile,
    checkinStatusTile,
    finishedStatusTile,
    canceledStatusTile,
  ]);

  return (
    <Box minWidth={150} display="flex" flexDirection={direction}>
      {isHeaderView ? (
        <>
          <Tooltip title={headerLabelTemplate} enterDelay={500}>
            <>{headerLabelTemplate}</>
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip title={tileLabelTemplate} enterDelay={500}>
            <>{tileLabelTemplate}</>
          </Tooltip>
        </>
      )}
      <TournamentStatusProgress
        status={tournament?.status}
        teamsSize={joinedTeamsSize}
        minTeamsSize={tournament?.teamsSettings?.minTeams}
        maxTeamsSize={tournament?.teamsSettings?.maxTeams}
      />
      {showStatus && <TournamentStatusLabel status={tournament?.status} />}
    </Box>
  );
};
